import { ChevronLeft, ChevronRight } from "lucide-react";
import React from "react";

interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T) => React.ReactNode;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  emptyMessage?: string;
  page?: number;
  total?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
}
export function Table<T>({
  columns,
  data,
  emptyMessage = "Nenhum registro encontrado.",
  page,
  totalPages,
  onPageChange,
}: TableProps<T>) {
  return (
    <div className="w-full overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="app-card overflow-hidden">
          <table className="w-full table-auto divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.key.toString()}
                    className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-500"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {data.length > 0 ? (
                data.map((item, index) => (
                  <tr key={index} className="transition hover:bg-slate-50/70">
                    {columns.map((col) => (
                      <td
                        key={col.key.toString()}
                        className="px-4 py-3 text-center text-sm text-slate-700 break-words max-w-[200px]"
                      >
                        {col.render
                          ? col.render(item)
                          : getValue(item, col.key)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-4 text-center text-sm text-slate-500"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages && onPageChange && page && (
        <div className="flex justify-center items-center mt-4 gap-2">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="btn-icon"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <span className="px-4 text-sm text-slate-700">
            {page} / {totalPages}
          </span>

          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="btn-icon"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
}

function getValue(item: any, key: any): string {
  const value = item[key];

  if (key === "createdAt" || key === "updatedAt") {
    try {
      const date = new Date(value);

      const dia = String(date.getDate()).padStart(2, "0");
      const mes = String(date.getMonth() + 1).padStart(2, "0");
      const ano = date.getFullYear();

      const hora = String(date.getHours()).padStart(2, "0");
      const minuto = String(date.getMinutes()).padStart(2, "0");
      const segundo = String(date.getSeconds()).padStart(2, "0");

      return `${dia}/${mes}/${ano} ${hora}:${minuto}:${segundo}`;
    } catch (error) {
      console.log(error);
      return item[key];
    }
  }

  return item[key];
}
